// list of all options and some reasonable defaults

// change default config with these constants:
const DEFAULT_KEY_FIELD = 'guid'
const DEFAULT_ITEMS_PER_PAGE = 20
const DEFAULT_SORT_OBJECT_TYPE = 'FieldsSortOrder'
const DEFAULT_SORT_FIELD_NAME = 'sortingList'
const DEFAULT_PAGINATION_OBJECT_TYPE = 'Pagination'

export default {
  // name to use as store module name
  // if left undefined, will be camelCased version of serviceClientKey without the end 'Client'
  // e.g. SessionServiceClient => sessionService
  name: undefined,
  // service will watch the authentication status and reset itself when it becomes false
  // set noAutoReset to true to prevent this (e.g. for userService which handles login)
  noAutoReset: false,
  // service params
  service: {
    // import of the service client and types
    // e.g. we have: import session_service from 'session_service_grpc_web_pb'
    // then: options.service.import = session_service
    import: undefined,
    // some types used in RPCs are not defined in the main import, rather in a separate file
    // all such other imports can be added here and functions will try to find them there also
    typeImports: [],
    // some fields (e.g. authenticationMethod.bearer) have different type than its name
    // we can add such overrides here, but also within individual actions
    typeMappings: {},
    // the following 3 params are given as-is to the service client upon initializing
    address: undefined,
    credentials: undefined,
    options: undefined,
    // the client field is used internally to do all requests
    // if not given, a new client will be created
    client: undefined,
    // the key of the client within the imported service
    // will be auto-inferred if undefined
    clientKey: undefined,
    // when sending GET requests, these GRPC objects are taken from import and filled in
    // sort is represented as array containing member params
    sortObjectType: DEFAULT_SORT_OBJECT_TYPE,
    sortFieldName: DEFAULT_SORT_FIELD_NAME,
    // if null, pagination parameters are not added via object but as normal fields
    // if undefined, no pagination is used
    paginationObjectType: DEFAULT_PAGINATION_OBJECT_TYPE,
    // when processing parameters, we will save flag/params to have easier check for features
    features: {
      // if defined, will be a function which instatiates sort object
      sorting: undefined,
      // if defined, will be a function which instatiates pagination object
      pagination: undefined
    }
  },

  // get metadata (called for every request)
  // must return empty object at minimum
  rpcMetadata: (config, name, request, context) => {
    return {}
  },

  // support master-detail relationship
  // if active item changes:
  //    - notify details-service about new masterKey
  //    - call get action on details-service to retrieve items
  // we use array so we can have master + N different details loaded indepentently
  detailsModules: [
    // {
    //   // name of the details store module
    //   name: 'documents',
    //   // after master key updates, we can call some actions on details service
    //   // params of the get action to retrieve the detailed items -> NOT INFERRED!
    //   onMasterKeyUpdate: [
    //     {
    //       name: 'getDocuments',
    //       // cache false -> every time get details is called we force API request
    //       cacheItems: false
    //     }
    //   ]
    // }
  ],

  items: {
    // list object type is inferred from name, if differes define it here
    // TODO: do we need this?
    objectType: undefined,
    // field used for comparison and merging of current cache
    keyField: DEFAULT_KEY_FIELD,
    // in some cases we will want to add another field/object as master key to the details, this field is used as override if defined
    keyFieldAsMaster: null,
    // master key field when the service is details for another one
    // if set, this field is automatically added to GET requests as additional filter
    masterKeyField: null,
    // if we are using infinite scroll we always fetch page=1 and set offset
    infiniteScroll: true,
    // how many items to fetch in one request; also applies to infinite scroll requests
    itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
    // where does sort/filtering happen?
    // if set to false, every change invokes new API request
    clientSearch: false,
    clientFiltering: false,
    clientSort: false,
    // these getters are added to the service during initialisation
    counts: {
      total: () => 0,
      filtered: () => 0
    }
  },

  // if set to true, no infer logic is applied; all actions must be manually defined
  manualActions: false,
  // set this to true to make all actions which are not explicitly configured triggers and skip inference
  allTriggerActions: false,
  // keyed actions and it's types and perhaps some other data
  // this is used to override infer logic
  actions: {
    // example:
    create: {
      // createSomething: {
      //   name: 'Create some' // by default we will apply lodash.startCase to action name
      // }
    },
    get: {},
    update: {
      // TODO: implement that updateItem can have fields configured - in this case, we don't send the whole object back, but only the field values
      // see also trigger comment
    },
    delete: {},
    download: {},
    trigger: {
      // an update is sometimes so specific that it's not sending the currently selected item but some other params
      // but once it returns, it can behave similarly - update active item or reload the details list (without performin a 2nd request to fetch the details!)
      // updateSearch: {
      //   updateActiveItem: {
      //     field: 'search'
      //   },
      //   updateDetailsModule: [{
      //     moduleName: 'documentService',
      //     listField: 'documentsList'
      //   }]
      // }
    }
  },

  // hooks allow us to jump into some parts of process while its happening
  // some are empty, some write to console by default
  // hooks are called from actions
  // logging actions (error, warning, info) are not awaited upon
  // hook can be async to call 3rd party logging service for example
  hooks: {
    // we can hook into any action before/after it gets the data (modify request or response)
    // this is useful to dispatch/commit updated data in adjacent services, set a different active item etc.
    // BEFORE hooks are ALWAYS called
    // ERROR hooks are called if request returns with an error
    // AFTER hooks are called if NO ERROR happened and only for latest request
    // hooks are async and are awaited before calling request done - but they shouldn't be long running, especially BEFORE hook
    get: {
      // example:
      // retrieveCustomers: async (config, context, request, params) => { console.log(request) }
      // if this hook returns, it must be a request object which overrides the given one
      beforeDataRequest: {},
      // example:
      // retrieveCustomers: async (config, context, request, params, data) { ... }
      afterDataRequest: {},
      // handle errors, same function signature
      error: {}
    },
    download: {
      beforeDataRequest: {},
      afterDataRequest: {},
      error: {}
    },
    trigger: {
      beforeDataRequest: {},
      afterDataRequest: {},
      error: {}
    },
    update: {
      // example:
      // retrieveCustomers: async (config, context, request, params) => { console.log(request) }
      // if this hook returns, it must be a request object which overrides the given one
      beforeDataRequest: {},
      // example:
      // retrieveCustomers: async (config, context, request, params, data) { ... }
      afterDataRequest: {},
      // handle errors, same function signature
      error: {}
    },
    // STANDARD BUILT-IN ACTIONS
    // if the currently active i.e. selected item changes, we call this hook
    standard: {
      selectActiveItem: {}
    },
    // AUTO LOAD
    // a function which gets called after authentication becomes true
    // example: context => { context.dispatch('getCategories') }
    autoLoad: null,
    // LOGGING ACTIONS ---- not awaited!
    // if we push an error we call this function
    error: (err) => {
      if (window && window.webpackHotUpdate) {
        // eslint-disable-next-line
        console.error(err)
      }
    },
    // if we push an warning we call this function
    info: (info) => {
      if (window && window.webpackHotUpdate) {
        // eslint-disable-next-line
        console.info(info)
      }
    },
    // if we push an warning we call this function
    warning: (warn) => {
      if (window && window.webpackHotUpdate) {
        // eslint-disable-next-line
        console.warn(warn)
      }
    }
  }
}
